import React from "react";

const Section3InformationCollection = () => (
  <>
    <h2>3. Information We Collect</h2>
    <h3>3.1 User Account Information</h3>
    <p>
    To facilitate the creation and management of user accounts on Comparedly.com, 
    we collect several categories of personal data directly from users. 
    This includes the user's full legal name, serving as a primary identifier. 
    We also collect the user's primary email address, which is essential for account 
    registration, communication, and password recovery. Comprehensive contact details, 
    such as a phone number and postal address, may also be collected to further 
    facilitate communication and verification processes. In the event that Comparedly.com 
    offers subscription-based services or involves transaction fees, we will collect 
    detailed billing information, including credit card details 
    (card number, expiry date, CVV), the associated billing address, and a record 
    of the user's payment history. Finally, for secure access to user accounts, 
    we collect unique login credentials, specifically usernames chosen by the user 
    and passwords that are securely encrypted within our systems.
    </p>

    <h3>3.2 Social Media Account Information</h3>
    <p>
    At the core of Comparedly.com's functionality is the ability to analyze social media 
    performance. To enable this, we collect specific information from the social media 
    accounts that users choose to connect to our platform. This includes the usernames 
    or handles associated with these connected accounts. We also access and process 
    profile data that is publicly available on these platforms, which may encompass 
    biographical information, website links, location details, and other profile elements 
    that users have made public. To gain the necessary access for data analysis, we 
    utilize secure access tokens obtained through industry-standard authorization protocols 
    such as OAuth. Additionally, we collect the unique identifiers assigned to the user 
    by the respective social media platforms for their connected accounts. Finally, 
    to provide meaningful analytics, we gather quantitative metrics related to the user's 
    social media presence, such as the number of followers, various engagement rates 
    (including likes, comments, and shares), and the overall reach of their content.
    </p>

    <h4>3.2.1 YouTube Data Usage</h4>
    <p>
      When you connect your YouTube account to Comparedly.com, we access and process certain data via the YouTube API Services. 
      This may include your channel ID, channel title, subscriber count, video statistics, and other publicly available or 
      authorized data necessary to deliver our analytics features.
    </p>
    <p>
      We use this data solely to provide insights into your channel’s performance and to help you benchmark and monitor your 
      YouTube presence. We do <strong>not</strong> use this data for advertising purposes, nor do we share it with third parties 
      unless required to provide our services or comply with legal obligations.
    </p>
    <p>
      All YouTube data accessed via the YouTube API Services is handled in accordance with:
      <ul>
        <li>
          The <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a>
        </li>
        <li>
          The <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>
        </li>
      </ul>
    </p>
    <p>
      You can revoke our access to your YouTube data at any time via your <a href="https://security.google.com/settings/security/permissions" target="_blank" rel="noopener noreferrer">Google security settings</a>.
    </p>

    <h3>3.3 Automatically Collected Information</h3>
    <p>
    When users interact with the comparedly.com website and utilize our analytics tool, 
    certain information is automatically collected from their devices and through our 
    systems. This includes the Internet Protocol (IP) address of the user's device, 
    which can provide general insights into their geographic location. We also automatically 
    collect detailed information about the user's device, such as the device type 
    (e.g., desktop computer, mobile phone), the operating system in use 
    (e.g., Windows, macOS, Android, iOS), and the type and version of the web browser 
    being used (e.g., Chrome, Safari, Firefox). Furthermore, we track the user's browsing 
    activity on the comparedly.com website, including the specific pages they visit, the 
    duration of their time spent on each page, the sequence in which they navigate through 
    the site, and their interactions with various website elements. We also collect 
    comprehensive usage data related to the analytics tool itself, monitoring the specific 
    features that users access, the frequency with which they utilize them, the actions 
    they take within the platform's interface, and performance metrics associated with the 
    tool's functionality. Like many online services, Comparedly.com employs cookies and 
    other similar tracking technologies (such as web beacons and pixels) to collect 
    information about users' browsing behavior, preferences, and device identifiers. 
    Finally, our systems automatically maintain comprehensive log files that record various 
    events and activities related to user access and platform usage, including timestamps 
    of access, records of any errors encountered, and data related to overall system 
    performance.
    </p>

    <h3>3.4 Payment Information (if applicable)</h3>
    <p>
    For users who subscribe to premium features or services offered by Comparedly.com, 
    we collect and process specific payment information to facilitate transactions. 
    This includes the user's complete credit card details, encompassing the full 
    card number, the expiration date, and the Card Verification Value (CVV). 
    We also collect the precise billing address associated with the payment method 
    provided by the user. Additionally, we maintain a detailed history of all payments 
    made by the user for the services rendered by Comparedly.com.
    </p>

    <h3>3.5 Information from Other Sources</h3>
    <p>
    To enhance user profiles, improve our services, and potentially for marketing outreach 
    (conducted with utmost care and in compliance with all applicable regulations), 
    Comparedly.com may, in limited circumstances, receive information about users from 
    other sources. This could include non-sensitive data acquired from reputable data 
    brokers who aggregate and sell information. When users explicitly choose to connect 
    their social media accounts to Comparedly.com, we may also receive publicly accessible 
    profile data directly from those social media platforms. Furthermore, we may receive 
    information from trusted partners who offer co-branded services or integrations with 
    Comparedly.com, always ensuring that such data sharing occurs with appropriate user 
    consent where necessary.
    </p>
  </>
);

export default Section3InformationCollection;
